import React from 'react';

import isPlainObject from 'lodash/isPlainObject';
import isString from 'lodash/isString';

import {
  Grid,
  Typography,
  MonthPicker,
  ConfirmDialog,
} from '@gogame/components';
import { useProcessAsync, useMountedState } from '@gogame/hooks-ts';
import { rangePresets } from '@gogame/intl-ts';
import { ScreenLink } from '@gogame/screens';

import {
  FILTER_STATUSES,
  FILTER_AFFILIATES,
  FILTER_BUSINESS_PARTNERS,
  FILTER_ADDED,
  FILTER_PERIOD,
  FILTER_ACTIONS_TYPE,
} from 'scenes/FinancialResults/constants';
import { financialResultsLink } from 'scenes/FinancialResults/navigation';
import { filtersSchema } from 'scenes/FinancialResults/schemas';
import IOTitle from 'scenes/IO/components/IOTitle';
import {
  TERMINATE_IO_STATUS_ALLOWED,
  TERMINATE_IO_STATUS_WARNING,
  TERMINATE_IO_STATUS_DISALLOWED,
} from 'scenes/IO/constants';
import { useCheckTerminateIoStatus } from 'scenes/IO/hooks';

const ConfirmationTerminateIODialog = ({
  title,
  io = {},
  confirmActionLabel,
  onSubmit,
  onClose,
  onExited,
  open,
}) => {
  const [frLinkParams, setFrLinkParams] = useMountedState('');
  const [terminateStatus, setTerminateStatus] = useMountedState(null);
  const [monthValue, setMonthValue] = useMountedState(undefined);
  const [loading, processAsync] = useProcessAsync();
  const checkTerminateIoStatus = useCheckTerminateIoStatus(processAsync);
  const isApply =
    terminateStatus === TERMINATE_IO_STATUS_ALLOWED ||
    terminateStatus === TERMINATE_IO_STATUS_WARNING;

  const handleSubmit = () => {
    onSubmit(monthValue);
  };

  const handleChangeMonth = (_e, selectedMonth) => {
    checkTerminateIoStatus({ id: io.id, date: selectedMonth })
      .then((response) => {
        const {
          affectedFinancialResultStatuses,
          period,
          terminationAvailabilityType,
        } = response;

        const affiliateIds = io?.affiliates?.map(({ id }) => `${id}`);
        const BPId = io?.businessPartner?.id;

        if (terminationAvailabilityType === TERMINATE_IO_STATUS_WARNING) {
          const filters = {
            ...filtersSchema.default(),
            [FILTER_ADDED]: rangePresets.LIFETIME_PLUS_FUTURE.value,
            [FILTER_PERIOD]: [period.from, period.to],
            [FILTER_ACTIONS_TYPE]: 0,
            [FILTER_STATUSES]: affectedFinancialResultStatuses,
          };
          if (BPId) {
            filters[FILTER_BUSINESS_PARTNERS] = [BPId];
          }
          if (affiliateIds.length) {
            filters[FILTER_AFFILIATES] = affiliateIds;
          }
          setFrLinkParams({ filters });
        }

        setTerminateStatus(terminationAvailabilityType);
      })
      .catch(() => {
        setTerminateStatus(null);
      });

    setMonthValue(selectedMonth);
  };

  return (
    <ConfirmDialog
      loading={loading}
      title={title}
      onClose={onClose}
      onSubmit={handleSubmit}
      onExited={onExited}
      open={open}
      confirmActionLabel={confirmActionLabel}
      disabledConfirmAction={!isApply}
    >
      <Grid direction={'column'} container spacing={2}>
        <Grid item>
          <Typography>
            <IOTitle businessPartner={io.businessPartner} period={io.period} />{' '}
            {isPlainObject(io.createdBy) ? 'by ' + io.createdBy.name : ''}{' '}
            {isString(io.createdBy) ? 'by ' + io.createdBy : ''}
          </Typography>
        </Grid>
        {terminateStatus === TERMINATE_IO_STATUS_DISALLOWED ? (
          <Grid item>
            <Typography>
              There are approved FR(s) for the selected month. Please choose
              another termination month.
            </Typography>
          </Grid>
        ) : null}
        {frLinkParams && terminateStatus === TERMINATE_IO_STATUS_WARNING ? (
          <Grid item>
            <Typography>
              <ScreenLink
                link={financialResultsLink}
                params={frLinkParams}
                target={'_blank'}
              >
                Pending FR(s)
              </ScreenLink>
              based on this IO will have "Pending IO" status after the
              termination.
            </Typography>
          </Grid>
        ) : null}

        <Grid item xs={4}>
          <MonthPicker
            value={monthValue}
            label={'Termination month (the last calendar month of IO validity)'}
            onChange={handleChangeMonth}
          />
        </Grid>
      </Grid>
    </ConfirmDialog>
  );
};

export default ConfirmationTerminateIODialog;
